// SettingsMenuDefaultBasicGeojsonStyle.js
import React, { useEffect, useState } from "react";
import { Box, Button, Collapsible, ColorPicker, createListCollection, Field, For, HStack, Image, Input, parseColor, Select, SimpleGrid, Text, useSelectContext } from "@chakra-ui/react";
import { capitalizeFirstLetter, rgbaToHex, saveToCache } from "../../../utilies/Utility";
import Type from "../../../utilies/Types";
import { useAppStore } from "../../../context/store";
import { applyGeojsonStylesToEntities } from "../../../utilies/GeojsonUtility";
import { useMapObjects } from "../../../context/MapObjectsContext";

const BasicGeojsonStyle = ({ data, title, keyG }) => {
  const setBasicGeojsonStyle = useAppStore.getState().setBasicGeojsonStyle;
  const [width, setWidth] = useState(data?.defaultLineWidth);
  const [priority, setPriority] = useState(data?.priority);
  const [outlineColor, setOutlineColor] = useState(data?.defaultOutlineColor);
  const [insideColor, setInsideColor] = useState(data?.defaultFillColor);

  useEffect(() => {
    setPriority(data?.priority);
    setWidth(data?.defaultLineWidth);
    setOutlineColor(data?.defaultOutlineColor);
    setInsideColor(data?.defaultFillColor);
  }, [data]);

  const styleTypeOption = createListCollection({
    items: [{ name: "Solid" }, { name: "Dashed" }, { name: "Dotted" }],
    itemToString: (item) => item.name,
    itemToValue: (item) => item.name,
  });

  const TypeSelectValue = () => {
    const select = useSelectContext();
    const items = select.selectedItems;
    if (items.length !== 0) {
      const { name } = items[0];
      return (
        <Select.ValueText placeholder="Select member">
          <HStack>{name}</HStack>
        </Select.ValueText>
      );
    }
  };

  const stylePatternsOption = createListCollection({
    items: [
      { name: "No Pattern", url: "" },
      { name: "Pattern 1", url: "/patterns/pattern1.svg" },
      { name: "Pattern 2", url: "/patterns/pattern2.svg" },
      { name: "Pattern 3", url: "/patterns/pattern3.svg" },
      { name: "Pattern 4", url: "/patterns/pattern4.svg" },
    ],
    itemToString: (item) => item.name,
    itemToValue: (item) => item.url,
  });

  return (
    <Collapsible.Root mb={1}>
      <Collapsible.Trigger paddingY="3" bg={"blackAlpha.100"} px={3} width={"full"} textAlign={"left"} cursor={"pointer"} borderRadius={"lg"} fontWeight={"medium"}>
        {title}
      </Collapsible.Trigger>

      <Collapsible.Content>
        <Box padding="4" borderWidth="1px" mt={1}>
          <SimpleGrid columns={3} gap={2} pb={2} px={3}>
            {data?.defaultLineWidth != null && data?.defaultLineWidth != undefined && (
              <Field.Root mb={2}>
                <Field.Label>Width</Field.Label>
                <Input
                  placeholder="Enter Line Width"
                  type="number"
                  value={width}
                  onChange={(v) => {
                    let w = "0";
                    if (v.target.value) w = v.target.value;

                    setWidth(w);

                    setBasicGeojsonStyle((prev) => {
                      const updated = {
                        ...prev,
                        [keyG]: {
                          ...prev[keyG],
                          defaultLineWidth: parseInt(w),
                        },
                      };

                      saveToCache(Type.STORAGE.BASIC_GEOJSON_STYLE, updated);
                      return updated;
                    });
                  }}
                />
              </Field.Root>
            )}

            {data?.defaultOutlineColor && (
              <ColorPicker.Root
                value={parseColor(outlineColor)}
                onValueChange={(v) => {
                  setOutlineColor(rgbaToHex(v.valueAsString));

                  setBasicGeojsonStyle((prev) => {
                    const updated = {
                      ...prev,
                      [keyG]: {
                        ...prev[keyG],
                        defaultOutlineColor: rgbaToHex(v.valueAsString),
                      },
                    };

                    saveToCache(Type.STORAGE.BASIC_GEOJSON_STYLE, updated);
                    return updated;
                  });
                }}
              >
                <ColorPicker.HiddenInput />
                <ColorPicker.Label>Outline Color</ColorPicker.Label>
                <ColorPicker.Control>
                  <ColorPicker.Input />
                  <ColorPicker.Trigger />
                </ColorPicker.Control>
                <ColorPicker.Positioner>
                  <ColorPicker.Content>
                    <ColorPicker.Area />
                    <HStack>
                      <ColorPicker.EyeDropper size="xs" variant="outline" />
                      <ColorPicker.Sliders />
                    </HStack>
                  </ColorPicker.Content>
                </ColorPicker.Positioner>
              </ColorPicker.Root>
            )}

            {data?.defaultFillColor && (
              <ColorPicker.Root
                value={parseColor(insideColor)}
                onValueChange={(v) => {
                  setInsideColor(rgbaToHex(v.valueAsString));

                  setBasicGeojsonStyle((prev) => {
                    const updated = {
                      ...prev,
                      [keyG]: {
                        ...prev[keyG],
                        defaultFillColor: rgbaToHex(v.valueAsString),
                      },
                    };

                    saveToCache(Type.STORAGE.BASIC_GEOJSON_STYLE, updated);
                    return updated;
                  });
                }}
              >
                <ColorPicker.HiddenInput />
                <ColorPicker.Label>Inside Color</ColorPicker.Label>
                <ColorPicker.Control>
                  <ColorPicker.Input />
                  <ColorPicker.Trigger />
                </ColorPicker.Control>
                <ColorPicker.Positioner>
                  <ColorPicker.Content>
                    <ColorPicker.Area />
                    <HStack>
                      <ColorPicker.EyeDropper size="xs" variant="outline" />
                      <ColorPicker.Sliders />
                    </HStack>
                  </ColorPicker.Content>
                </ColorPicker.Positioner>
              </ColorPicker.Root>
            )}

            {data?.defaultStyle && (
              <Select.Root
                collection={styleTypeOption}
                size="sm"
                defaultValue={[capitalizeFirstLetter(data?.defaultStyle)]}
                positioning={{ sameWidth: true }}
                onValueChange={(v) => {
                  setBasicGeojsonStyle((prev) => {
                    const updated = {
                      ...prev,
                      [keyG]: {
                        ...prev[keyG],
                        defaultStyle: v.value[0],
                      },
                    };

                    saveToCache(Type.STORAGE.BASIC_GEOJSON_STYLE, updated);
                    return updated;
                  });
                }}
              >
                <Select.HiddenSelect />
                <Select.Label>Type</Select.Label>
                <Select.Control>
                  <Select.Trigger>
                    <TypeSelectValue />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>
                <Select.Positioner>
                  <Select.Content>
                    {styleTypeOption.items.map((item) => (
                      <Select.Item item={item} key={item.name} justifyContent="flex-start">
                        {item.name}
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>
            )}

            {data?.defaultPattren && (
              <Select.Root
                collection={stylePatternsOption}
                size="sm"
                defaultValue={[data?.defaultPattren]}
                positioning={{ sameWidth: true }}
                onValueChange={(v) => {
                  setBasicGeojsonStyle((prev) => {
                    const updated = {
                      ...prev,
                      [keyG]: {
                        ...prev[keyG],
                        defaultPattren: v.value[0],
                      },
                    };

                    saveToCache(Type.STORAGE.BASIC_GEOJSON_STYLE, updated);
                    return updated;
                  });
                }}
              >
                <Select.HiddenSelect />
                <Select.Label>Fill Pattern</Select.Label>
                <Select.Control>
                  <Select.Trigger>
                    <TypeSelectValue />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>
                <Select.Positioner>
                  <Select.Content>
                    {stylePatternsOption.items.map((item) => (
                      <Select.Item item={item} key={item.name} justifyContent="flex-start">
                        {item.url && <Image src={item.url} />}
                        {item.name}
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>
            )}

            {data?.priority && data?.priority != 0 && (
              <Field.Root mb={2}>
                <Field.Label>Priority</Field.Label>
                <Input
                  placeholder="Enter Priority"
                  type="number"
                  value={priority}
                  onChange={(v) => {
                    let w = "1";
                    if (v.target.value) w = v.target.value;

                    setPriority(w);

                    setBasicGeojsonStyle((prev) => {
                      const updated = {
                        ...prev,
                        [keyG]: {
                          ...prev[keyG],
                          priority: parseInt(w),
                        },
                      };

                      saveToCache(Type.STORAGE.BASIC_GEOJSON_STYLE, updated);
                      return updated;
                    });
                  }}
                />
              </Field.Root>
            )}
          </SimpleGrid>
        </Box>
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

function SettingsMenuDefaultBasicGeojsonStyle() {
  const basicGeojsonStyle = useAppStore((s) => s.basicGeojsonStyle);
  const viewerRef = useAppStore((s) => s.viewerRef);
  const [searchText, setSearchText] = useState("");
  const { mapObjects, setMapObjects } = useMapObjects();

  const applyChanges = () => {
    applyGeojsonStylesToEntities(viewerRef.current, basicGeojsonStyle, mapObjects, setMapObjects);
  };

  return (
    <>
      <Box>
        <Field.Root mb={2}>
          <Input
            placeholder="Search ..."
            type="text"
            value={searchText}
            onChange={(v) => {
              setSearchText(v.target.value);
            }}
          />
        </Field.Root>

        <For each={Object.entries(basicGeojsonStyle).filter(([, item]) => (item?.title || "").toLowerCase().includes(searchText.toLowerCase()))}>
          {([key, item]) => <BasicGeojsonStyle key={key} data={item} title={item.title} keyG={key} />}
        </For>
      </Box>

      <HStack justify={"space-between"} width={"full"} pt={2}>
        <Text>These settings are saved automatically</Text>
        <Button colorPalette={"black"} onClick={applyChanges}>
          Apply
        </Button>
      </HStack>
    </>
  );
}

export default SettingsMenuDefaultBasicGeojsonStyle;
